import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { Mutation, Query } from '@lgg/isomorphic/types/__generated__/graphql';
import {
  PERSONAL_API_KEY_QUERY,
  REGENERATE_PERSONAL_API_KEY_MUTATION,
} from 'src/components/domain/users/components/profile-settings-modal/operations';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';

export const useApiKeySettings = () => {
  const { t } = useTranslation(['user']);
  const showNotification = useShowNotification();

  const [plaintextToken, setPlaintextToken] = useState<string | null>(null);

  const { data, loading: queryLoading } =
    useQuery<Pick<Query, 'personalApiKey'>>(PERSONAL_API_KEY_QUERY);

  const [regeneratePersonalApiKey, { loading: mutationLoading }] = useMutation<
    Pick<Mutation, 'regeneratePersonalApiKey'>
  >(REGENERATE_PERSONAL_API_KEY_MUTATION);

  const maskedToken = data?.personalApiKey?.masked ?? null;
  const hasExistingKey = maskedToken !== null;

  const handleRegenerate = useCallback(async () => {
    await regeneratePersonalApiKey({
      onCompleted: (data) => {
        setPlaintextToken(data.regeneratePersonalApiKey.apiKey);
        showNotification({
          type: 'success',
          title: t('user:profileSettings.sections.apiKey.toasts.success'),
        });
      },
      onError: () => {
        showNotification({
          type: 'error',
          title: t('user:profileSettings.sections.apiKey.toasts.error'),
        });
      },
      refetchQueries: [{ query: PERSONAL_API_KEY_QUERY }],
    });
  }, [regeneratePersonalApiKey, showNotification, t]);

  const handleReset = useCallback(() => {
    setPlaintextToken(null);
  }, []);

  return {
    queryLoading,
    mutationLoading,
    hasExistingKey,
    maskedToken,
    plaintextToken,
    handleRegenerate,
    handleReset,
  };
};
